import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import QuestionsListResponseEntity from "../entities/QuestionsListResponseEntity";
import QuestionsListResponseSchema from "../schemas/QuestionsListResponseSchema";
import QuestionsStore from "../stores/QuestionsStore";

interface GetQuestionsRequestParamsDTO {
    productId: number;
    offset?: number;
}

@injectable()
class QuestionsService {
    constructor(
        private questionsStore: QuestionsStore,
        private readonly apiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}
    private getQuestions(
        reqParams: GetQuestionsRequestParamsDTO
    ): Observable<QuestionsListResponseEntity> {
        this.logger.debug(
            "QuestionsService.getQuestionsByProductId is started",
            reqParams
        );

        const status =
            this.questionsStore.getStatus() === STATUS.VIRGIN
                ? STATUS.INITIAL_LOADING
                : STATUS.LOADING;
        this.questionsStore.setStatus(status);

        return this.apiClient
            .get({
                url: "/question/getQuestionList",
                responseType: "json",
                queryParams: {
                    productId: reqParams.productId,
                    offset: reqParams.offset || 0,
                },
            })
            .pipe(
                map((v) => create(v.body, QuestionsListResponseSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "QuestionsService.getQuestionsByProductId is errored",
                        { reqParams, error }
                    );
                    applyTransaction(() => {
                        this.questionsStore.setError(error);
                        this.questionsStore.setStatus(STATUS.ERROR);
                    });
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "QuestionsService.getQuestionsByProductId is ready",
                        reqParams
                    );
                    this.questionsStore.setStatus(STATUS.READY);
                })
            );
    }
    getQuestionsByProductId(
        reqParams: GetQuestionsRequestParamsDTO
    ): Observable<QuestionsListResponseEntity> {
        return this.getQuestions(reqParams).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.questionsStore.set(v.items);
                    this.questionsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                });
            })
        );
    }

    loadMoreQuestionsByProductId(
        reqParams: GetQuestionsRequestParamsDTO
    ): Observable<QuestionsListResponseEntity> {
        return this.getQuestions(reqParams).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.questionsStore.add(v.items);
                    this.questionsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                });
            })
        );
    }
}

export default QuestionsService;
